/**
 * agentStatus — one plain-English vocabulary for a teammate's live state.
 *
 * Every agent carries a `status` of `idle | working | waiting_founder | blocked
 * | off` (plus a `kill_switch` a founder flips with Pause/Resume). Those are
 * engineer words. Two surfaces show them to founders — the Agents roster and an
 * agent's detail page — and until now each spelled them itself, so they drifted:
 * the same `waiting_founder` read "Waiting" on the roster but "Waiting on
 * founder" on the detail page, and `idle`/`off` leaked as "Idle"/"Off", which a
 * non-technical founder can read as *broken* rather than *free* / *paused*.
 *
 * This is the single source of truth both pages import, so the words can never
 * drift again. `agentStatusChip` takes the whole agent (status + kill_switch)
 * so a paused teammate reads one honest "Paused" chip instead of "Off" — and it
 * returns the chip classes too, so the amber "Waiting on you" pulse (the signal
 * that THIS teammate needs the founder) shows identically everywhere.
 *
 * Anything unrecognised falls back to a de-slugged Title-case string so a new
 * status value can never leak a raw slug to a founder.
 *
 * Kept dependency-free so it is unit-tested directly (tests/agent-status.test.ts).
 */
import type { AgentStatus } from '@shared/types';

export interface AgentStatusChip {
  /** short chip phrase a founder can read at a glance */
  label: string;
  /** chip-* colour + modifier classes (drives the colour and the pulsing dot) */
  cls: string;
  /** one-line plain decode, used as the chip's hover/accessible title */
  tip: string;
}

/** status code → the founder-facing chip. */
const CHIPS: Record<AgentStatus, AgentStatusChip> = {
  working: { label: 'Working', cls: 'chip-ice live', tip: 'On a task right now.' },
  waiting_founder: {
    label: 'Waiting on you',
    cls: 'chip-amber urgent',
    tip: 'Needs your approval or answer before it can move on.',
  },
  blocked: {
    label: 'Blocked',
    cls: 'chip-coral alarm',
    tip: 'Stuck on something outside its control (like a missing login) — see "Needs your eyes".',
  },
  idle: { label: 'Resting', cls: 'chip-dim', tip: 'Free and ready — nothing queued right now.' },
  off: { label: 'Paused', cls: 'chip-dim', tip: 'Stopped — tap Resume to bring them back.' },
};

/** A paused teammate (kill_switch on) always reads the same, matching the Pause/Resume button. */
const PAUSED: AgentStatusChip = {
  label: 'Paused',
  cls: 'chip-dim',
  tip: 'You paused this teammate — tap Resume to bring them back.',
};

/** De-slug an unknown status: 'foo-bar' → 'Foo bar'. */
function deslug(s: string): string {
  const words = String(s).replace(/[-_]+/g, ' ').trim();
  if (!words) return 'Unknown';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The one chip both the roster and the detail page render for a teammate. */
export function agentStatusChip(a: { status: AgentStatus; kill_switch?: boolean | number }): AgentStatusChip {
  if (a.kill_switch) return PAUSED;
  return CHIPS[a.status] ?? { label: deslug(a.status), cls: 'chip-dim', tip: '' };
}
